<script setup>
  import { computed, ref, watch } from "vue";
  import { storeToRefs } from "pinia";
  import { LayoutTemplate } from "lucide-vue-next";
  import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
  import { Button } from "@/components/ui/button";
  import { Badge } from "@/components/ui/badge";
  import SearchInput from "@/components/episto-ui/search-input/SearchInput.vue";
  import { EmptyState } from "@/components/episto-ui/empty-state";
  import { useMCreativeStore } from "@stores/mCreative";
  import { useMTemplatesStore } from "@stores/mTemplates";

  const open = defineModel("open", { type: Boolean, default: false });

  const { mCreative } = storeToRefs(useMCreativeStore());
  const mTemplatesStore = useMTemplatesStore();
  const { mTemplates } = storeToRefs(mTemplatesStore);
  const { setTemplateIds } = mTemplatesStore;

  const searchQuery = ref("");
  const initialIds = ref([]);
  const draftIds = ref([]);

  watch(open, (isOpen) => {
    if (!isOpen) return;
    initialIds.value = [...(mCreative.value.template_ids || [])];
    draftIds.value = [...initialIds.value];
    searchQuery.value = "";
  });

  const offeredTemplates = computed(() =>
    mTemplates.value.filter((template) => !template.archived_at || initialIds.value.includes(template.id)),
  );

  const filteredTemplates = computed(() => {
    const query = searchQuery.value.trim().toLowerCase();
    if (!query) return offeredTemplates.value;
    return offeredTemplates.value.filter((template) => template.name.toLowerCase().includes(query));
  });

  const hasDraftChanges = computed(
    () =>
      draftIds.value.length !== initialIds.value.length ||
      draftIds.value.some((id, index) => id !== initialIds.value[index]),
  );

  const isDraftSelected = (id) => draftIds.value.includes(id);

  const toggleDraft = (id) => {
    draftIds.value = isDraftSelected(id) ? draftIds.value.filter((el) => el !== id) : [...draftIds.value, id];
  };

  const close = () => (open.value = false);

  const save = () => {
    setTemplateIds(draftIds.value);
    close();
  };
</script>

<template>
  <Dialog v-model:open="open">
    <DialogContent
      :show-close-button="false"
      class="flex h-[calc(100vh-40px)] max-w-[calc(100vw-60px)] flex-col gap-0 p-0">
      <div class="flex items-center justify-between gap-4 border-b px-6 py-4">
        <div class="flex items-center gap-3">
          <DialogTitle class="heading-title">Template library</DialogTitle>
          <DialogDescription class="sr-only">Pick the templates used by this creative.</DialogDescription>
          <Badge variant="outline">
            {{ filteredTemplates.length }} template{{ filteredTemplates.length === 1 ? "" : "s" }}
          </Badge>
        </div>
        <div class="flex items-center gap-3">
          <span
            v-if="draftIds.length"
            class="text-sm text-muted-foreground">
            {{ draftIds.length }} selected
          </span>
          <Button
            variant="outline"
            @click="close">
            {{ hasDraftChanges ? "Cancel" : "Close" }}
          </Button>
          <Button
            v-if="hasDraftChanges"
            @click="save">
            Save changes
          </Button>
        </div>
      </div>
      <section class="flex flex-1 flex-col gap-3 overflow-y-auto p-6">
        <SearchInput
          v-model="searchQuery"
          placeholder="Search by name"
          full-width />
        <EmptyState
          v-if="filteredTemplates.length === 0"
          title="No template found"
          :icon="LayoutTemplate" />
        <ul
          v-else
          class="m-0 grid list-none grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-4 p-0">
          <li
            v-for="template in filteredTemplates"
            :key="template.id"
            :data-testid="`template-library-item-${template.name}`"
            class="flex cursor-pointer flex-col gap-2"
            @click="toggleDraft(template.id)">
            <div
              class="aspect-square overflow-hidden rounded-md border-2 bg-muted"
              :class="isDraftSelected(template.id) ? 'border-primary' : 'border-border'">
              <img
                class="block h-full w-full object-contain"
                :src="template.preview_url"
                :alt="template.name" />
            </div>
            <span class="truncate text-sm">{{ template.name }}</span>
          </li>
        </ul>
      </section>
    </DialogContent>
  </Dialog>
</template>
